/**
 * npeAllocationsApi — RTK Query slice cho /purchase/npe-allocations/*.
 * Tag types: 'NPEAlloc'. Cũng invalidate 'NPEFixation' (count) và 'LocalP' khi commit.
 *
 * Wizard flow (SOP §7.8):
 *  1. POST /sessions/create { fixation_id } -> nhận session_uid + lines snapshot
 *  2. PUT  /sessions/{uid}/lines { lines: [...] } (debounced 500ms khi user gõ)
 *  3. POST /sessions/{uid}/commit -> tạo committed allocations + invalidate
 *  4. DELETE /sessions/{uid} (cancel)
 *  5. DELETE /{allocation_id} — soft-delete 1 committed allocation
 */
import { baseApi } from './baseApi'
import type {
  DataResponse,
  MessageResponse,
  NPEAllocationCommitIn,
  NPEAllocationLineIn,
  NPEAllocationListQuery,
  NPEAllocationOut,
  NPEAllocationSessionState,
  PageResponse,
} from '@/types/api'

export const npeAllocationsApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listNPEAllocations: b.query<
      PageResponse<NPEAllocationOut>,
      NPEAllocationListQuery | void
    >({
      query: (params) => ({
        url: '/purchase/npe-allocations/list',
        params: params ?? {},
      }),
      providesTags: (res, _e, arg) => {
        const fix = (arg && (arg as NPEAllocationListQuery).fixation_no) || 'ALL'
        const list = res
          ? [
              ...res.items.map((a) => ({
                type: 'NPEAlloc' as const,
                id: a.allocation_id,
              })),
              { type: 'NPEAlloc' as const, id: `LIST-${fix}` },
            ]
          : [{ type: 'NPEAlloc' as const, id: `LIST-${fix}` }]
        return [...list, { type: 'NPEAlloc' as const, id: 'LIST' }]
      },
    }),
    /** Đọc nhanh allocations theo fixation_no (dùng cho NPEFixationDetail / wizard step 2). */
    getNPEAllocationsByFixation: b.query<DataResponse<NPEAllocationOut[]>, string>({
      query: (fixationNo) =>
        `/purchase/npe-allocations/by-fixation/${encodeURIComponent(fixationNo)}`,
      providesTags: (_r, _e, fixationNo) => [
        { type: 'NPEAlloc', id: `by-fix-${fixationNo}` },
      ],
    }),

    /** ---- Session APIs ---- */
    createAllocationSession: b.mutation<
      DataResponse<NPEAllocationSessionState>,
      { fixation_id: number }
    >({
      query: (body) => ({
        url: '/purchase/npe-allocations/sessions/create',
        method: 'POST',
        body,
      }),
      // Không invalidate gì (session chưa commit, không thay đổi list)
    }),
    getAllocationSession: b.query<DataResponse<NPEAllocationSessionState>, string>({
      query: (sessionUid) => `/purchase/npe-allocations/sessions/${sessionUid}`,
      providesTags: (_r, _e, uid) => [{ type: 'NPEAlloc', id: `session-${uid}` }],
    }),
    updateSessionLines: b.mutation<
      DataResponse<NPEAllocationSessionState>,
      { sessionUid: string; lines: NPEAllocationLineIn[]; note?: string | null; advancedDate?: string | null }
    >({
      query: ({ sessionUid, lines, note, advancedDate }) => ({
        url: `/purchase/npe-allocations/sessions/${sessionUid}/lines`,
        method: 'PUT',
        body: { lines, note: note ?? undefined, advanced_date: advancedDate ?? undefined },
      }),
      // Cập nhật cache session theo response
      async onQueryStarted({ sessionUid }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            npeAllocationsApi.util.updateQueryData(
              'getAllocationSession',
              sessionUid,
              (draft) => {
                if (data?.data) draft.data = data.data
              },
            ),
          )
        } catch {
          // ignore
        }
      },
    }),
    commitAllocationSession: b.mutation<
      DataResponse<NPEAllocationOut[]>,
      { sessionUid: string; body: NPEAllocationCommitIn }
    >({
      query: ({ sessionUid, body }) => ({
        url: `/purchase/npe-allocations/sessions/${sessionUid}/commit`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'NPEAlloc', id: 'LIST' },
        { type: 'NPEFixation', id: 'LIST' },
        { type: 'LocalP', id: 'LIST' },
        { type: 'LocalPForward', id: 'LIST' },
      ],
    }),
    cancelAllocationSession: b.mutation<MessageResponse, string>({
      query: (sessionUid) => ({
        url: `/purchase/npe-allocations/sessions/${sessionUid}`,
        method: 'DELETE',
      }),
    }),

    /** Soft-delete 1 committed allocation. */
    deleteNPEAllocation: b.mutation<MessageResponse, number>({
      query: (allocationId) => ({
        url: `/purchase/npe-allocations/${allocationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'NPEAlloc', id },
        { type: 'NPEAlloc', id: 'LIST' },
        { type: 'NPEFixation', id: 'LIST' },
      ],
    }),

    /** Bulk soft-delete all allocations cho fixation_no (Access btnDelete). */
    deleteNPEAllocationsByFixation: b.mutation<MessageResponse, string>({
      query: (fixationNo) => ({
        url: `/purchase/npe-allocations/by-fixation/${encodeURIComponent(fixationNo)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'NPEAlloc', id: 'LIST' },
        { type: 'NPEFixation', id: 'LIST' },
        { type: 'LocalP', id: 'LIST' },
        { type: 'LocalPForward', id: 'LIST' },
      ],
    }),
  }),
})

export const {
  useListNPEAllocationsQuery,
  useGetNPEAllocationsByFixationQuery,
  useCreateAllocationSessionMutation,
  useGetAllocationSessionQuery,
  useUpdateSessionLinesMutation,
  useCommitAllocationSessionMutation,
  useCancelAllocationSessionMutation,
  useDeleteNPEAllocationMutation,
  useDeleteNPEAllocationsByFixationMutation,
} = npeAllocationsApi
